import React, { useState, useEffect } from 'react';
import Header from '../../components/common/Header';
import styles from './Dashboard.module.css';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';

const Dashboard: React.FC = () => {
  const { token, user } = useAuth();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [audits, setAudits] = useState<any[]>([]);

  useEffect(() => {
    const fetchAudits = async () => {
      try {
        const resp = await fetch('/api/audits', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await resp.json();
        if (data.success) {
          setAudits(data.audits);
        }
      } catch (e) { console.error(e); }
    };

    const resolveCancellations = async () => {
      const params = new URLSearchParams(window.location.search);
      const canceledSessionId = params.get('canceled_session');
      if (canceledSessionId && token) {
        try {
          await fetch('/api/audits/cancel', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ session_id: canceledSessionId })
          });
          // Optionally clean the URL without reloading
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (e) {
          console.error("Failed to cleanly cancel the checkout session state.", e);
        }
      }
    };

    if (token) {
      resolveCancellations().then(fetchAudits);
    }
  }, [token]);

  const startCheckout = async () => {
    if (!url) return;
    setLoading(true);
    try {
      const resp = await fetch('/api/payment/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ targetUrl: url })
      });
      const data = await resp.json();
      if (data.url) {
        window.location.href = data.url; // Redirect to Stripe Checkout
      }
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const executeDownload = async (auditId: string) => {
    try {
      const resp = await api.get<any>(`/api/audits/${auditId}/download`);
      if (resp.success && resp.data?.url) {
        window.open(resp.data.url, '_blank');
      } else {
        console.error('Failed to generate secure URL', resp.error);
      }
    } catch (err) {
      console.error('Error fetching presigned url:', err);
    }
  };

  return (
    <div className={styles.dashboard}>
      <Header />
      <main className={styles.content} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100%', paddingTop: '100px', paddingBottom: '100px' }}>

        {/* Previous Analyses prioritize rendering above the generic checkout loop */}
        {audits.filter(a => a.status !== 'pending' && a.status !== 'canceled').length > 0 && (
          <div style={{ maxWidth: '600px', width: '100%', padding: '32px', background: '#ffffff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', marginBottom: '2rem' }}>
            <h2 style={{ margin: '0 0 1.5rem 0', paddingBottom: '1rem', borderBottom: '1px solid #eee', color: '#333' }}>Active & Past Action Plans</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {audits.filter(a => a.status !== 'pending' && a.status !== 'canceled').map((audit) => (
                <div key={audit._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f9f9f9', padding: '16px', borderRadius: '8px', border: '1px solid #eee' }}>
                  <div>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>{audit.targetUrl}</h4>
                    <div style={{ color: '#888', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{new Date(audit.createdAt).toLocaleDateString()}</span>
                      <span>&middot;</span>
                      <span style={{
                        color: audit.status === 'completed' ? '#00c853' : audit.status === 'failed' ? '#ff4444' : '#ffb800',
                        textTransform: 'capitalize',
                        fontWeight: '600'
                      }}>
                        {audit.status}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    {audit.status === 'completed' ? (
                      <button
                        onClick={() => executeDownload(audit._id)}
                        style={{ border: 'none', cursor: 'pointer', background: '#4285f4', color: '#fff', padding: '10px 16px', borderRadius: '4px', textDecoration: 'none', fontSize: '0.9rem', fontWeight: '500', transition: 'background 0.2s', whiteSpace: 'nowrap' }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#357abd'}
                        onMouseOut={(e) => e.currentTarget.style.background = '#4285f4'}
                      >
                        Download PDF
                      </button>
                    ) : audit.status !== 'failed' && (
                      <a
                        href={`/dashboard/audit?session_id=${audit.stripeSessionId}`}
                        style={{ background: '#111', color: '#4ade80', padding: '10px 16px', borderRadius: '4px', textDecoration: 'none', fontSize: '0.9rem', fontWeight: '600', transition: 'background 0.2s', whiteSpace: 'nowrap' }}
                        onMouseOver={(e) => { e.currentTarget.style.background = '#222'; e.currentTarget.style.color = '#00ff00'; }}
                        onMouseOut={(e) => { e.currentTarget.style.background = '#111'; e.currentTarget.style.color = '#4ade80'; }}
                      >
                        View Live Terminal ⚡
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Audit Trigger is inherently placed beneath active workflows */}
        <div style={{ maxWidth: '600px', width: '100%', padding: '32px', background: '#ffffff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ marginBottom: '1rem', color: '#333' }}>Build New Action Plan</h2>
          <p style={{ marginBottom: '2rem', color: '#666', lineHeight: '1.5' }}>Welcome {user?.name || 'User'}! Enter a target URL to run the AI Visibility analysis and generate your personalized developer-ready Action Plan.</p>

          <input
            type="url"
            placeholder="https://your-website.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            style={{ width: '100%', padding: '12px', fontSize: '16px', marginBottom: '1.5rem', borderRadius: '4px', border: '1px solid #ddd', background: '#fff', color: '#333', boxSizing: 'border-box' }}
          />

          <button
            onClick={startCheckout}
            disabled={loading || !url}
            style={{ width: '100%', padding: '12px', fontSize: '16px', background: '#4285f4', color: '#fff', border: 'none', borderRadius: '4px', cursor: loading || !url ? 'not-allowed' : 'pointer', fontWeight: '500', opacity: loading || !url ? 0.7 : 1, transition: 'all 0.2s ease' }}
          >
            {loading ? 'Initializing Checkout...' : 'Run Comprehensive Audit - $29'}
          </button>
        </div>

      </main>
    </div>
  );
};

export default Dashboard;
